import { defineStore } from 'pinia'
import { ref, computed, markRaw } from 'vue'
import { MODAL_COMPONENTS, EMPTY_MODAL_STATE } from '@/constants/modals'
import type { ModalName, ModalStateOrEmpty, ModalDataMap, ModalState } from '@/types/modal'

export const useModalsStore = defineStore('modals', () => {
  const modal = ref<ModalStateOrEmpty>(EMPTY_MODAL_STATE)
  const isSubmitting = ref<boolean>(false)

  const currentComponent = computed(() => modal.value.component)
  const currentModalData = computed(() => modal.value.data)

  function close(): void {
    modal.value = EMPTY_MODAL_STATE
    isSubmitting.value = false
  }

  /**
   * Opens a modal by name with data.
   * Data parameter is optional only for modals where ModalDataMap[T] is undefined.
   *
   * Note: Type assertion required due to TypeScript limitations:
   * - `as ModalState`: TypeScript can't narrow discriminated unions with generics
   */
  function open<T extends ModalName>(
    modalName: T,
    ...args: ModalDataMap[T] extends undefined ? [] : [data: ModalDataMap[T]]
  ): void {
    modal.value = {
      name: modalName,
      data: args[0],
      component: markRaw(MODAL_COMPONENTS[modalName])
    } as ModalState
  }

  const modalModelValue = computed<boolean>({
    get: () => modal.value.name !== null,
    set: (value: boolean) => {
      if (!value) {
        close()
      }
    }
  })

  return {
    modal,
    currentComponent,
    currentModalData,
    modalModelValue,
    isSubmitting,
    open,
    close
  }
})
